import { useEffect, useState } from 'react';

/**
 * The signed-in dashboard is hash-routed: `#fleet/<tenantId>/<section>`. Each
 * fleet + section pair is its own URL, so the fleet switcher and sidebar are
 * real links and the browser/mouse Back-Forward traverse them (same pattern as
 * the admin console). Signed-out routes (`#sign-in` / `#start`) are unaffected.
 */
export type FleetSection =
  | 'overview'
  | 'launcher'
  | 'broadcasts'
  | 'devices'
  | 'telemetry'
  | 'audit'
  | 'subscription';

export const FLEET_SECTIONS: readonly FleetSection[] = [
  'overview',
  'launcher',
  'broadcasts',
  'devices',
  'telemetry',
  'audit',
  'subscription',
];

export interface FleetRoute {
  tenantId: string | null;
  section: FleetSection;
}

export function parseFleetHash(): FleetRoute {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const parts = raw.split('/');
  if (parts[0] !== 'fleet') return { tenantId: null, section: 'overview' };
  const tenantId = parts[1] ? decodeURIComponent(parts[1]) : null;
  const sec = parts[2] ?? 'overview';
  const section = (FLEET_SECTIONS as readonly string[]).includes(sec) ? (sec as FleetSection) : 'overview';
  return { tenantId, section };
}

export function fleetHref(tenantId: string, section: FleetSection): string {
  return `#fleet/${encodeURIComponent(tenantId)}/${section}`;
}

/** Programmatic navigation — assigning the hash pushes a history entry. */
export function goFleet(tenantId: string, section: FleetSection): void {
  window.location.hash = `fleet/${encodeURIComponent(tenantId)}/${section}`;
}

/** Replace (no history entry) — for the initial default landing. */
export function replaceFleet(tenantId: string, section: FleetSection): void {
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}${fleetHref(tenantId, section)}`);
}

export function useFleetRoute(): FleetRoute {
  const [route, setRoute] = useState<FleetRoute>(() => parseFleetHash());
  useEffect(() => {
    const on = (): void => setRoute(parseFleetHash());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}
